import { Dispatch, FunctionComponent, SetStateAction, useCallback, useEffect, useRef, useState } from "react";

import { Backdrop, Box, Grid, Stack } from "@mui/material";
import { Nullable } from "@simplyk/common";
import { useSearchParams } from "next/navigation";
import { FormProvider, useWatch } from "react-hook-form";

import { RecipientEditor } from "../../features/Contact/RecipientEditor";
import { useRecipientPlaceholder } from "../EmailRecipientEditor/useRecipientPlaceholder";

import { StripeEmailAlert } from "./Components/EmailAlert/StripeEmailAlert";
import { VerifyEmailAlert } from "./Components/EmailAlert/VerifyEmailAlert";
import { EmailFooter } from "./Components/EmailFooter/EmailFooter";
import { EmailPreview } from "./Components/EmailPreview/EmailPreview";
import { EmailRecipientLabel } from "./Components/EmailRecipients/EmailRecipientLabel";
import { EmailRecipientsField } from "./Components/EmailRecipients/EmailRecipientsField";
import { EmailRecipientsFieldRef } from "./Components/EmailRecipients/types";
import { useEmailEditor } from "./hooks/useEmailEditor";
import { useEmailEditorForm } from "./hooks/useEmailEditorForm";
import { useThankYouEmailGeneration } from "./hooks/useThankYouEmailGeneration";
import { EmailContent } from "./types";
import { useStyles } from "./useStyles";

import { OutlinedMailFieldWysiwyg } from "@/components/OutlinedMailFieldWysiwyg/OutlinedMailFieldWysiwyg";
import { FroalaConfig } from "@/components/OutlinedTextFieldWysiwyg/type";
import { AmplitudeEvents } from "@/constants/amplitude";
import { DashboardButton } from "@/dashboard-design-system/Button";
import { DashboardDrawer } from "@/dashboard-design-system/Drawer";
import { DashboardIcon } from "@/dashboard-design-system/Icon/DashboardIcon";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { DashboardZeffyLoader } from "@/dashboard-design-system/ZeffyLoader/DashboardZeffyLoader";
import { isRequired } from "@/helpers/validators";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useTranslate } from "@/hooks/useTranslate";
import { ArrowLeft, Success } from "@/icons/outlined";
import { useCurrentUserContext } from "@/src/contexts/CurrentUserContext";
import { useEmailEditorContext } from "@/src/contexts/EmailEditorContext";

export type EmailEditorProps = {
  onClose: () => void;
  refetch: () => Promise<void>;
  previewOpened: boolean;
  closePreview: () => void;
  setRecipientsCount: Dispatch<SetStateAction<number>>;
  emailContent: EmailContent;
  onEmailGenerated?: (body: string) => void;
};

export const EmailEditorSsr: FunctionComponent<React.PropsWithChildren<EmailEditorProps>> = (props) => {
  const { onClose, refetch, previewOpened, closePreview, setRecipientsCount, emailContent, onEmailGenerated } = props;
  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();
  const classes = useStyles();
  const { currentUser, isSendingEmailForbidden, organization } = useCurrentUserContext();
  const subjectRef = useRef<HTMLInputElement | null>(null);
  const params = useSearchParams();
  const thankYouTransactionId = emailContent.thankYouTransactionId || (params.get("thankYouTransactionId") as string);

  const { getThankYouEmailFromCache } = useEmailEditorContext();

  const emailRecipientFieldRef = useRef<Nullable<EmailRecipientsFieldRef>>(null);

  const cachedBody = thankYouTransactionId ? getThankYouEmailFromCache(thankYouTransactionId) : undefined;
  const emailContentWithCache = cachedBody ? { ...emailContent, body: cachedBody } : emailContent;

  // Determine if we need to auto-generate thank you email content
  const isThankYouEmail = Boolean(thankYouTransactionId);
  const hasEmptyBody = !emailContentWithCache.body || emailContentWithCache.body.trim() === "";
  const shouldAutoGenerate = isThankYouEmail && hasEmptyBody && !cachedBody;

  const [isGeneratingEmail, setIsGeneratingEmail] = useState(shouldAutoGenerate);

  const { form, formDefaultValues, handleBodyChange } = useEmailEditorForm({
    emailContent: emailContentWithCache,
    isThankYouEmail,
  });
  const { handleSendEmail, loading } = useEmailEditor({
    form,
    refetch,
    onClose,
    emailContent,
    thankYouEmailId: thankYouTransactionId,
  });

  const { generateEmail, cancelGeneration } = useThankYouEmailGeneration({
    form,
    handleBodyChange,
    onEmailGenerated,
    onLoadingChange: setIsGeneratingEmail,
    emailContentId: emailContent.id,
  });

  // Auto-generate AI email content if needed
  useEffect(() => {
    const autoGenerateEmail = async () => {
      if (!shouldAutoGenerate || !thankYouTransactionId) {
        return;
      }

      await generateEmail(thankYouTransactionId);
    };

    autoGenerateEmail();

    // Cleanup: cancel generation on unmount to prevent state updates
    return () => {
      cancelGeneration();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateRecipientEmails = useCallback(
    async (validEmails: string[]) => {
      const newEmails = validEmails.map((email) => email.toLowerCase());
      form.setValue("recipientEmails", newEmails, { shouldDirty: true });
    },
    [form]
  );

  const body = useWatch({ control: form.control, name: "body" });
  const subject = useWatch({ control: form.control, name: "subject" });
  const recipientEmails = useWatch({ control: form.control, name: "recipientEmails" });
  const recipientsCount = recipientEmails?.length || 0;

  useEffect(() => {
    setRecipientsCount(recipientsCount);
  }, [recipientsCount, setRecipientsCount]);

  const recipientPlaceholder = useRecipientPlaceholder({
    emailType: emailContent?.emailType,
    hasRecipients: Boolean(recipientsCount),
  });

  if (!emailContent) {
    return <EmailLoader />;
  }

  return (
    <>
      <FormProvider {...form}>
        <Grid container sx={{ height: "100%" }}>
          <Grid item xs={previewOpened && !isSmallScreen ? 6 : undefined} height="100%" maxWidth="100%">
            <Grid container direction="column" sx={{ height: "100%", flexWrap: "nowrap" }}>
              <Grid item xs sx={{ overflowY: "auto" }}>
                <Grid container height="100%" direction="column" flexWrap="nowrap" paddingLeft={2} paddingRight={2}>
                  <Grid item style={{ maxWidth: "100%" }}>
                    <Grid container>
                      {isSendingEmailForbidden && !currentUser?.emailVerified ? (
                        <Grid item xs={12} marginBottom={1}>
                          <VerifyEmailAlert needToLinkBankAccount />
                        </Grid>
                      ) : (
                        <>
                          {!currentUser?.emailVerified && (
                            <Grid item xs={12} marginBottom={1}>
                              <VerifyEmailAlert />
                            </Grid>
                          )}
                          {isSendingEmailForbidden && (
                            <Grid item xs={12} marginBottom={1}>
                              <StripeEmailAlert />
                            </Grid>
                          )}
                        </>
                      )}
                      <Grid item xs={12} marginBottom={3}>
                        {recipientPlaceholder && (
                          <EmailRecipientLabel hiddenHelperText hiddenTitle={false}>
                            <RecipientEditor placeholder={recipientPlaceholder} />
                          </EmailRecipientLabel>
                        )}
                        {!recipientPlaceholder && isThankYouEmail && (
                          <EmailRecipientLabel hiddenHelperText hiddenTitle={false} hiddenTooltip>
                            <Box
                              data-test="email-recipient-chip-list"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                padding: "12px 16px",
                                borderRadius: "8px",
                                backgroundColor: (theme) => theme.palette.surface.neutral.supershy,
                              }}
                            >
                              <DashboardIcon vibe="text-positive-quiet" size="small">
                                <Success />
                              </DashboardIcon>
                              <DashboardTypography vibe="text-neutral-moderate" variant="body2">
                                {recipientEmails?.[0]}
                              </DashboardTypography>
                            </Box>
                          </EmailRecipientLabel>
                        )}
                        {!recipientPlaceholder && !isThankYouEmail && (
                          <EmailRecipientsField
                            recipientEmails={recipientEmails || []}
                            updateRecipientEmails={updateRecipientEmails}
                            emailContentId={emailContent.id}
                            ref={emailRecipientFieldRef}
                            onDrawerOpen={() => closePreview()}
                            disabled={Boolean(thankYouTransactionId)}
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs sx={{ width: "100%" }} paddingBottom={2}>
                    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                      <Grid item marginBottom={2}>
                        <DashboardTextField
                          data-test="email-editor-subject-input"
                          name="subject"
                          defaultValue={formDefaultValues.subject}
                          control={form.control}
                          debounced={250}
                          errorMessage={form.formState.errors["subject"]?.message}
                          inputProps={{ ref: subjectRef }}
                          label={t("dashboard", "EmailEditor.subject")}
                        />
                      </Grid>
                      <div style={{ flex: 1, position: "relative" }}>
                        <OutlinedMailFieldWysiwyg
                          data-test="email-editor-body-input"
                          className={classes.wysiwyg}
                          keyName="mailFieldModal"
                          defaultValue={formDefaultValues.body}
                          name="body"
                          subjectName="subject"
                          subjectRef={subjectRef}
                          // This makes sure the body is updated when set from
                          // templates within the wysiwyg.
                          onChange={handleBodyChange}
                          height="100%"
                          heightMax="100%"
                          heightMinOver310={310}
                          addVariableAmplitudeEvent={AmplitudeEvents.EmailEditorAddVariableClicked}
                          rules={[isRequired(t("common", "bodyRequired") as string)]}
                          error={form.formState.errors["body"]}
                          zIndex={10000}
                          attachmentKeyName="attachment"
                          config={thankYouTransactionId ? FroalaConfig.ExtraThankYouEmail : FroalaConfig.Email}
                        />
                        {isGeneratingEmail && (
                          <Backdrop
                            open={isGeneratingEmail}
                            sx={{
                              position: "absolute",
                              backgroundColor: "rgba(255, 255, 255, 0.9)",
                              zIndex: 10001,
                            }}
                          >
                            <Stack direction="column" alignItems="center" gap={2}>
                              <Box
                                component="img"
                                src="/gifs/ai-crafting-email.gif"
                                alt="Generating email"
                                sx={{ width: 200, height: 200 }}
                              />
                              <DashboardButton
                                variant="outlined"
                                vibe="neutral"
                                onClick={() => cancelGeneration(thankYouTransactionId)}
                              >
                                {t("common", "cancel")}
                              </DashboardButton>
                            </Stack>
                          </Backdrop>
                        )}
                      </div>
                    </div>
                  </Grid>
                </Grid>
              </Grid>
              <Grid
                item
                paddingTop={2}
                paddingLeft={2}
                paddingRight={2}
                width="100%"
                sx={(theme) => ({ borderTop: 1, borderColor: theme.palette.border.neutral.quiet })}
              >
                <EmailFooter
                  form={form}
                  handleSendEmail={handleSendEmail}
                  loading={loading}
                  emailType={emailContent.emailType}
                  emailContent={{ ...emailContent, thankYouTransactionId }}
                  isGenerating={isGeneratingEmail}
                  generateEmail={generateEmail}
                />
              </Grid>
            </Grid>
          </Grid>
          {previewOpened && !isSmallScreen && (
            <Grid item xs={6} paddingTop={3} paddingLeft={4} sx={{ height: "100%", overflowY: "auto" }}>
              <EmailPreview
                body={body || ""}
                subject={subject || ""}
                senderName={organization?.name || ""}
                emailContentId={emailContent.id}
                recipientEmails={typeof recipientEmails === "string" ? [] : recipientEmails || []}
              />
            </Grid>
          )}
        </Grid>
        {previewOpened && isSmallScreen && (
          <DashboardDrawer
            open={previewOpened}
            onClose={closePreview}
            title={t("dashboard", "preview")}
            slots={{
              closeIcon: <ArrowLeft />,
            }}
            closePlacement="left"
          >
            <Grid container justifyContent="center" height="100%">
              <EmailPreview
                body={body || ""}
                subject={subject || ""}
                senderName={organization?.name || ""}
                emailContentId={emailContent.id}
                recipientEmails={typeof recipientEmails === "string" ? [] : recipientEmails || []}
              />
            </Grid>
          </DashboardDrawer>
        )}
      </FormProvider>
    </>
  );
};

export const EmailLoader = () => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: 1,
        height: "100%",
      }}
    >
      <DashboardZeffyLoader size="large" />
    </Box>
  );
};
